import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  createStyles,
  Flex,
  Input,
  Table,
  Text,
  Badge,
  Button,
  Loader,
  ActionIcon,
  Select,
  TextInput,
  Grid,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useSelector } from "react-redux";
import {
  IconSearch,
  IconDownload,
  IconChevronDown,
  IconChevronUp,
  IconEdit,
  IconFilter,
  IconFilterOff,
} from "@tabler/icons-react";
import moment from "moment";
import useDebounce from "../../utils/useDebounce";
import { decrypt } from "../../utils";
import CustomBillingPagination from "../Pagination/CustomBillingPagination";
import { getEvvRecords } from "../../services/UserTracking";
import { getAllStaff } from "../../services/staff";
import { getAllPatientsModal } from "../../services/patient";
import { getAllFacilitiesModal } from "../../services/facility";
import EvvDownloadFilterModal from "./EvvDownloadFilterModal";
import EditEvvRecordDrawer from "./EditEvvRecordDrawer";

const useStyles = createStyles(() => ({
  search: {
    background: "var(--app-surface)",
    borderRadius: "12px",
  },
  tableWrapper: {
    overflowX: "auto",
  },
  headerRow: {
    backgroundColor: "var(--app-surface-2)",
  },
  headerCell: {
    fontFamily: "Inter",
    fontSize: "12px",
    fontWeight: 600,
    color: "var(--app-text-muted)",
    textTransform: "uppercase",
    padding: "12px 8px",
    whiteSpace: "nowrap",
  },
  bodyCell: {
    fontFamily: "Inter",
    fontSize: "13px",
    color: "var(--app-text)",
    padding: "10px 8px",
    whiteSpace: "nowrap",
  },
  clickableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "var(--app-surface-2)",
    },
  },
  detailBox: {
    background: "var(--app-surface-2)",
    padding: "12px 16px",
    borderRadius: "8px",
    margin: "4px 0 8px 0",
  },
  detailLabel: {
    fontFamily: "Inter",
    fontSize: "11px",
    fontWeight: 600,
    color: "var(--app-text-muted)",
  },
  detailValue: {
    fontFamily: "Inter",
    fontSize: "12px",
    color: "var(--app-text)",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 0",
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
  },
  filterBar: {
    background: "var(--app-surface)",
    padding: "12px 16px",
    borderRadius: "12px",
    marginBottom: "10px",
    border: "1px solid var(--app-border)",
  },
}));

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return moment(dateStr).format("MMM D, YYYY");
};

const formatTime = (dateStr) => {
  if (!dateStr) return "N/A";
  return moment(dateStr).format("hh:mm A");
};

const formatDistance = (distance) => {
  if (distance == null) return "N/A";
  return `${Number(distance).toFixed(2)} mi`;
};

const getStaffName = (staff) => {
  if (!staff) return "N/A";
  return (
    staff?.name ||
    `${staff?.firstName || ""} ${staff?.lastName || ""}`.trim() ||
    "N/A"
  );
};

const getClientName = (client) => {
  if (!client) return "N/A";
  const f1 = client?.form1;
  if (f1?.firstName || f1?.lastName) {
    return `${f1.firstName || ""} ${f1.lastName || ""}`.trim();
  }
  if (f1?.name) return f1.name;
  if (client?.name) {
    try {
      return decrypt(client.name, false) || client.name;
    } catch {
      return client.name;
    }
  }
  return "N/A";
};

const EvvDetailRow = ({ record, classes, onEdit }) => {
  const [open, setOpen] = useState(false);

  const renderEvvDetail = (evv, label) => {
    if (!evv) return null;
    return (
      <Box mb={8}>
        <Text fw={600} fz={12} color="var(--app-text)" mb={4}>
          {label}
        </Text>
        <Flex gap={16} wrap="wrap">
          <Box>
            <Text className={classes.detailLabel}>Original Timestamp</Text>
            <Text className={classes.detailValue}>
              {evv.timestamp
                ? moment(evv.timestamp).format("MMM D, YYYY hh:mm:ss A")
                : "N/A"}
            </Text>
          </Box>
          <Box>
            <Text className={classes.detailLabel}>Lat / Lng</Text>
            <Text className={classes.detailValue}>
              {evv.location?.lat ?? "N/A"} / {evv.location?.lng ?? "N/A"}
            </Text>
          </Box>
          <Box>
            <Text className={classes.detailLabel}>Accuracy</Text>
            <Text className={classes.detailValue}>
              {evv.location?.accuracy != null
                ? `${Number(evv.location.accuracy).toFixed(1)}m`
                : "N/A"}
            </Text>
          </Box>
          <Box>
            <Text className={classes.detailLabel}>Matched Address</Text>
            <Text className={classes.detailValue}>
              {evv.matchedAddress || "N/A"}
            </Text>
          </Box>
          <Box>
            <Text className={classes.detailLabel}>Service Address</Text>
            <Text className={classes.detailValue}>
              {evv.serviceAddress || "N/A"}
            </Text>
          </Box>
          <Box>
            <Text className={classes.detailLabel}>Device Type</Text>
            <Text className={classes.detailValue}>
              {evv.deviceInfo?.deviceType || "N/A"}
            </Text>
          </Box>
          <Box>
            <Text className={classes.detailLabel}>Location Source</Text>
            <Text className={classes.detailValue}>
              {evv.deviceInfo?.locationSource || "N/A"}
            </Text>
          </Box>
          <Box>
            <Text className={classes.detailLabel}>IP</Text>
            <Text className={classes.detailValue}>
              {evv.deviceInfo?.ip || "N/A"}
            </Text>
          </Box>
        </Flex>
      </Box>
    );
  };

  return (
    <>
      <tr className={classes.clickableRow} onClick={() => setOpen(!open)}>
        <td className={classes.bodyCell}>
          <ActionIcon size="sm" variant="subtle">
            {open ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
          </ActionIcon>
        </td>
        <td className={classes.bodyCell}>{getStaffName(record?.staff)}</td>
        <td className={classes.bodyCell}>{record?.facility?.name || "N/A"}</td>
        <td className={classes.bodyCell}>
          <Badge
            size="sm"
            variant="light"
            color={record?.checkin_type === "client" ? "blue" : "gray"}
          >
            {record?.checkin_type || "N/A"}
          </Badge>
        </td>
        <td className={classes.bodyCell}>{getClientName(record?.client)}</td>
        <td className={classes.bodyCell}>{record?.service || "N/A"}</td>
        <td className={classes.bodyCell}>{formatDate(record?.date?.date)}</td>
        <td className={classes.bodyCell}>{formatTime(record?.startTime)}</td>
        <td className={classes.bodyCell}>
          {record?.endTime ? (
            formatTime(record.endTime)
          ) : (
            <Badge size="sm" variant="light" color="orange">
              Still Clocked In
            </Badge>
          )}
        </td>
        <td className={classes.bodyCell}>
          {formatDistance(record?.evvClockIn?.distanceToSite)}
        </td>
        <td className={classes.bodyCell}>
          {formatDistance(record?.evvClockOut?.distanceToSite)}
        </td>
        <td className={classes.bodyCell}>
          <Badge
            size="sm"
            variant="light"
            color={record?.evvEnforced ? "green" : "gray"}
          >
            {record?.evvEnforced ? "Yes" : "No"}
          </Badge>
        </td>
        <td className={classes.bodyCell}>
          <ActionIcon
            size="sm"
            variant="subtle"
            color="yellow"
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.(record);
            }}
          >
            <IconEdit size={14} />
          </ActionIcon>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={13} style={{ padding: "0 8px 8px 8px" }}>
            <Box className={classes.detailBox}>
              {renderEvvDetail(record?.evvClockIn, "Clock-In EVV Details")}
              {renderEvvDetail(record?.evvClockOut, "Clock-Out EVV Details")}
              {!record?.evvClockIn && !record?.evvClockOut && (
                <Text fz={12} color="dimmed">
                  No EVV location data recorded
                </Text>
              )}
            </Box>
          </td>
        </tr>
      )}
    </>
  );
};

const EvvRecordsTab = ({ range }) => {
  const { classes } = useStyles();
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [staffSearch, setStaffSearch] = useState("");
  const debouncedStaffSearch = useDebounce(staffSearch, 1000);
  const [
    downloadModalOpened,
    { open: openDownloadModal, close: closeDownloadModal },
  ] = useDisclosure(false);
  const [editOpened, { open: openEdit, close: closeEdit }] =
    useDisclosure(false);
  const [editRecord, setEditRecord] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const perPage = 10;

  // Filter state
  const [filterStaffId, setFilterStaffId] = useState(null);
  const [filterClientId, setFilterClientId] = useState(null);
  const [filterFacilityId, setFilterFacilityId] = useState(null);
  const [filterCheckinType, setFilterCheckinType] = useState(null);
  const [filterService, setFilterService] = useState("");
  const [filterEvvEnforced, setFilterEvvEnforced] = useState(null);

  // Redux data for filter dropdowns
  const allStaff = useSelector((state) => state.staff?.allStaff) || [];
  const allPatients = useSelector((state) => state.patient?.allPatients) || [];
  const facilityState = useSelector((state) => state.facility);
  const allFacilities =
    facilityState?.allFacilities?.facilities ||
    facilityState?.allDeletedFacilities?.facilities ||
    [];

  // Fetch dropdown data on mount
  useEffect(() => {
    if (!allStaff.length) getAllStaff("all", false, undefined, "");
    if (!allPatients.length) getAllPatientsModal("all", false);
    if (!allFacilities.length) getAllFacilitiesModal("all", false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const staffOptions = allStaff
    ?.filter((s) => s?.active === true && s?.deleted === false)
    ?.map((s) => ({
      value: s._id,
      label:
        `${s.firstName || ""} ${s.lastName || ""}`.trim() || s.name || s._id,
    }));

  const clientOptions = allPatients
    ?.filter((c) => c?.active === true && c?.deleted === false)
    .map((c) => {
      const f1 = c?.form1;
      const label =
        f1?.firstName || f1?.lastName
          ? `${f1.firstName || ""} ${f1.lastName || ""}`.trim()
          : c?.name || c?._id;
      return { value: c?.id || c?._id, label };
    });

  const facilityOptions = allFacilities
    ?.filter((f) => f?.active === true && f?.deleted === false)
    .map((f) => ({
      value: f?.id || f?._id,
      label: f?.name || f?.id,
    }));

  const hasActiveFilters =
    filterStaffId ||
    filterClientId ||
    filterFacilityId ||
    filterCheckinType ||
    filterService ||
    filterEvvEnforced;

  const buildQuery = useCallback(() => {
    const query = {};
    if (range?.[0]) query.dateFrom = moment(range[0]).format("YYYY-MM-DD");
    if (range?.[1]) query.dateTo = moment(range[1]).format("YYYY-MM-DD");
    if (debouncedStaffSearch) query.staffName = debouncedStaffSearch;
    if (filterStaffId) query.staffId = filterStaffId;
    if (filterClientId) query.clientId = filterClientId;
    if (filterFacilityId) query.facilityId = filterFacilityId;
    if (filterCheckinType) query.checkinType = filterCheckinType;
    if (filterService) query.service = filterService;
    if (filterEvvEnforced) query.evvEnforced = filterEvvEnforced;
    return query;
  }, [
    range,
    debouncedStaffSearch,
    filterStaffId,
    filterClientId,
    filterFacilityId,
    filterCheckinType,
    filterService,
    filterEvvEnforced,
  ]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const query = { page: currentPage, perPage, ...buildQuery() };
    try {
      const data = await getEvvRecords(query);
      setRecords(data?.records || []);
      setTotal(data?.total || 0);
    } catch (err) {
      // silently handled
    } finally {
      setLoading(false);
    }
  }, [currentPage, buildQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedStaffSearch,
    range,
    filterStaffId,
    filterClientId,
    filterFacilityId,
    filterCheckinType,
    filterService,
    filterEvvEnforced,
  ]);

  const handleEdit = (record) => {
    setEditRecord(record);
    openEdit();
  };

  const clearFilters = () => {
    setFilterStaffId(null);
    setFilterClientId(null);
    setFilterFacilityId(null);
    setFilterCheckinType(null);
    setFilterService("");
    setFilterEvvEnforced(null);
  };

  return (
    <Box>
      <Flex align="center" justify="space-between" gap={10} my={10} wrap="wrap">
        <Flex align="center" gap={10} sx={{ flex: 1 }}>
          <Box className={classes.search} sx={{ flex: 1, maxWidth: 300 }}>
            <Input
              radius="md"
              icon={<IconSearch size={16} />}
              placeholder="Search Staff Name"
              onChange={(e) => setStaffSearch(e.target.value)}
              value={staffSearch}
            />
          </Box>
          <Button
            size="xs"
            variant={showFilters ? "filled" : "light"}
            color="yellow"
            c="var(--app-on-primary)"
            leftIcon={
              showFilters ? (
                <IconFilterOff size={14} />
              ) : (
                <IconFilter size={14} />
              )
            }
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "Hide Filters" : "Filters"}
            {hasActiveFilters
              ? ` (${[filterStaffId, filterClientId, filterFacilityId, filterCheckinType, filterService, filterEvvEnforced].filter(Boolean).length})`
              : ""}
          </Button>
        </Flex>
        <Button
          size="xs"
          leftIcon={<IconDownload size={14} />}
          onClick={openDownloadModal}
          disabled={loading}
          color="yellow"
          c="var(--app-on-primary)"
        >
          Download
        </Button>
      </Flex>

      {showFilters && (
        <Box className={classes.filterBar}>
          <Grid gutter="sm">
            <Grid.Col span={4}>
              <Select
                size="xs"
                label="Staff"
                placeholder="All staff"
                data={staffOptions}
                value={filterStaffId}
                onChange={setFilterStaffId}
                searchable
                clearable
                nothingFound="No staff found"
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <Select
                size="xs"
                label="Client"
                placeholder="All clients"
                data={clientOptions}
                value={filterClientId}
                onChange={setFilterClientId}
                searchable
                clearable
                nothingFound="No clients found"
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <Select
                size="xs"
                label="Facility"
                placeholder="All facilities"
                data={facilityOptions}
                value={filterFacilityId}
                onChange={setFilterFacilityId}
                searchable
                clearable
                nothingFound="No facilities found"
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <Select
                size="xs"
                label="Type"
                placeholder="All types"
                data={[
                  { value: "facility", label: "Facility" },
                  { value: "client", label: "Client" },
                ]}
                value={filterCheckinType}
                onChange={setFilterCheckinType}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput
                size="xs"
                label="Service"
                placeholder="Filter by service"
                value={filterService}
                onChange={(e) => setFilterService(e.target.value)}
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <Select
                size="xs"
                label="EVV Enforced"
                placeholder="All"
                data={[
                  { value: "true", label: "Yes" },
                  { value: "false", label: "No" },
                ]}
                value={filterEvvEnforced}
                onChange={setFilterEvvEnforced}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <Flex align="flex-end" h="100%">
                <Button
                  size="xs"
                  variant="subtle"
                  color="gray"
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                >
                  Clear Filters
                </Button>
              </Flex>
            </Grid.Col>
          </Grid>
        </Box>
      )}

      <Box className={classes.tableWrapper}>
        {loading ? (
          <Flex justify="center" align="center" py={40}>
            <Loader size="md" color="yellow" />
          </Flex>
        ) : records.length === 0 ? (
          <Box className={classes.emptyState}>
            <Text fz={14}>No EVV records found</Text>
          </Box>
        ) : (
          <Table highlightOnHover>
            <thead>
              <tr className={classes.headerRow}>
                <th className={classes.headerCell} style={{ width: 40 }} />
                <th className={classes.headerCell}>Staff Name</th>
                <th className={classes.headerCell}>Facility</th>
                <th className={classes.headerCell}>Type</th>
                <th className={classes.headerCell}>Client</th>
                <th className={classes.headerCell}>Service</th>
                <th className={classes.headerCell}>Date</th>
                <th className={classes.headerCell}>Clock-In</th>
                <th className={classes.headerCell}>Clock-Out</th>
                <th className={classes.headerCell}>In Distance</th>
                <th className={classes.headerCell}>Out Distance</th>
                <th className={classes.headerCell}>EVV Enforced</th>
                <th className={classes.headerCell} style={{ width: 50 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <EvvDetailRow
                  key={record?._id}
                  record={record}
                  classes={classes}
                  onEdit={handleEdit}
                />
              ))}
            </tbody>
          </Table>
        )}
      </Box>

      <CustomBillingPagination
        data={total}
        itemsPerPage={perPage}
        currentPage={currentPage}
        handlePageChange={setCurrentPage}
      />

      <EvvDownloadFilterModal
        opened={downloadModalOpened}
        onClose={closeDownloadModal}
        buildQuery={buildQuery}
        facilityName={
          filterFacilityId
            ? allFacilities.find((f) => (f?.id || f?._id) === filterFacilityId)
                ?.name || null
            : null
        }
      />

      <EditEvvRecordDrawer
        opened={editOpened}
        onClose={closeEdit}
        record={editRecord}
        onSuccess={fetchData}
      />
    </Box>
  );
};

export default EvvRecordsTab;
